import { type ReactElement } from 'react';
import { TextField, type FieldDensity } from '@csa/ui';

/**
 * The justification.
 *
 * Required on everything, not only on what needs approving. It is the field
 * an auditor reads first, and a field collected only for privileged actions
 * is one nobody learns to write properly -- so when it finally matters, it
 * says "fix".
 *
 * Ten characters is a deliberately low bar. A higher one produces padding,
 * not reasons; the real quality control is that an approver reads it and a
 * reviewer can search it.
 */

export const JUSTIFICATION_MINIMUM = 10;
const MAXIMUM = 1_000;

export function isJustified(value: string): boolean {
  return value.trim().length >= JUSTIFICATION_MINIMUM;
}

export function JustificationField({
  value,
  onChange,
  className,
  density = 'default',
  hint = 'Recorded in the audit trail and shown to approvers.',
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** `compact` in the console headers, where this sits in a single dense row. */
  density?: FieldDensity;
  hint?: string;
}): ReactElement {
  const length = value.trim().length;
  const tooShort = length > 0 && length < JUSTIFICATION_MINIMUM;

  return (
    <TextField
      label="Why are you running this?"
      density={density}
      hint={hint}
      error={tooShort ? `At least ${JUSTIFICATION_MINIMUM} characters.` : undefined}
      containerClassName={className ?? 'min-w-72 flex-1'}
      value={value}
      maxLength={MAXIMUM}
      onChange={(event) => onChange(event.target.value)}
      placeholder="e.g. Investigating INC-4821, customer reports a missing order"
      /**
       * Autofill off. The browser would offer the previous justification,
       * and a reason supplied by autocomplete is worse than none -- it
       * reads as considered and is not.
       */
      autoComplete="off"
    />
  );
}
